import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/store';
import { PlusIcon, SearchIcon, EditIcon, EyeIcon, FilterIcon, RefreshCwIcon, AlertCircleIcon } from 'lucide-react';
import toast from 'react-hot-toast';
import { patientsApi, Patient, PatientsQueryParams } from '../../lib/api';
import AddPatientModal from '../../components/Patients/AddPatientModal';
import PatientDetailsModal from '../../components/Patients/PatientDetailsModal';
import EditPatientModal from '../../components/Patients/EditPatientModal';
import { useSettings } from '../../contexts/SettingsContext';

const PatientsPage = () => {
  // Get current user context
  const { user: currentUser } = useSelector((state: RootState) => state.auth);
  
  // Settings for date formatting
  const { dateFormat, initialized } = useSettings();

  // Check if current user is a doctor or nurse (read-only access)
  const isDoctor = currentUser?.role?.toUpperCase() === 'DOCTOR';
  const isNurse = currentUser?.role?.toUpperCase() === 'NURSE';
  const canEdit = !isDoctor && !isNurse; // Only admins and receptionists can edit patients

  // State management
  const [patients, setPatients] = useState<Patient[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [itemsPerPage] = useState(10);
  const [genderFilter, setGenderFilter] = useState<string>('');
  const [sortBy, setSortBy] = useState<string>('createdAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [showFilters, setShowFilters] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedPatientId, setSelectedPatientId] = useState<string | null>(null);
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);

  // Fetch patients with current filters and pagination
  const fetchPatients = useCallback(async () => {
    setLoading(true);
    try {
      const params: PatientsQueryParams = {
        page: currentPage,
        limit: itemsPerPage,
        sortBy,
        sortOrder,
      };
      if (searchTerm.trim()) params.search = searchTerm.trim();
      if (genderFilter) params.gender = genderFilter;
      const response = await patientsApi.getPatients(params);
      if (response.data.success && response.data.data) {
        setPatients(response.data.data.patients);
        setTotalPages(response.data.data.pagination.totalPages);
        setTotalItems(response.data.data.pagination.totalItems);
      } else {
        throw new Error(response.data.message || 'Failed to fetch patients');
      }
    } catch (err: any) {
      console.error(err.response?.data?.message || err.message || 'Failed to fetch patients');
    } finally {
      setLoading(false);
    }
  }, [currentPage, itemsPerPage, searchTerm, genderFilter, sortBy, sortOrder]);

  // Main fetch effect
  useEffect(() => {
    fetchPatients();
  }, [fetchPatients]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (currentPage !== 1) {
        setCurrentPage(1);
      } else {
        fetchPatients();
      }
    }, 500);
    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  // Handle refresh
  const handleRefresh = () => {
    fetchPatients();
    toast.success('Patients list refreshed');
  };

  // Handle sorting
  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  // Handle view details
  const handleViewDetails = (patient: Patient) => {
    setSelectedPatientId(patient.id);
    setShowDetailsModal(true);
  };

  // Handle edit patient
  const handleEditPatient = (patient: Patient) => {
    setSelectedPatient(patient);
    setShowEditModal(true);
  };

  // Calculate age from date of birth
  const calculateAge = (dateOfBirth: string) => {
    const today = new Date();
    const birthDate = new Date(dateOfBirth);
    let age = today.getFullYear() - birthDate.getFullYear();
    const monthDiff = today.getMonth() - birthDate.getMonth();
    
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    
    return age;
  };

  // Format gender for display
  const formatGender = (gender: string) => {
    return gender.toLowerCase().charAt(0).toUpperCase() + gender.toLowerCase().slice(1);
  };

  // Format date according to user preferences
  const formatDate = useMemo(() => {
    return (dateString: string) => {
      const date = new Date(dateString);
      const format = initialized ? dateFormat : 'MM/DD/YYYY';
      
      const day = String(date.getDate()).padStart(2, '0');
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const year = date.getFullYear();
      
      switch (format) {
        case 'DD/MM/YYYY':
          return `${day}/${month}/${year}`;
        case 'YYYY-MM-DD':
          return `${year}-${month}-${day}`;
        case 'MM/DD/YYYY':
        default:
          return `${month}/${day}/${year}`;
      }
    };
  }, [dateFormat, initialized]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900">Patients</h1>
          <p className="text-gray-600">
            {(isDoctor || isNurse)
              ? `View patient records and information (${totalItems} total patients)`
              : `Manage patient records and information (${totalItems} total patients)`
            }
          </p>
        </div>
        <div className="flex gap-2">
          <button onClick={handleRefresh} className="btn-secondary">
            <RefreshCwIcon className="h-4 w-4" />
          </button>
          {canEdit && (
            <button 
              onClick={() => setShowAddModal(true)}
              className="btn-primary w-full sm:w-auto"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Add Patient
            </button>
          )}
        </div>
      </div>

      {/* Search and Filters */}
      <div className="bg-white rounded-lg shadow p-4 lg:p-6">
        <div className="space-y-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              {loading && searchTerm ? (
                <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                </div>
              ) : (
                <SearchIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              )}
              <input
                type="text"
                placeholder="Search patients by name, email, phone, or patient number..."
                className="input pl-10 pr-10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
              {searchTerm && (
                <button
                  onClick={() => setSearchTerm('')}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600"
                  title="Clear search"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              )}
            </div>
            <button 
              onClick={() => setShowFilters(!showFilters)}
              className={`btn-secondary w-full sm:w-auto ${showFilters ? 'bg-primary-50 border-primary-200' : ''}`}
            >
              <FilterIcon className="h-4 w-4 mr-2" />
              Filters
            </button>
          </div>

          {/* Advanced Filters */}
          {showFilters && (
            <div className="border-t pt-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Gender</label>
                <select
                  value={genderFilter}
                  onChange={(e) => setGenderFilter(e.target.value)}
                  className="input"
                >
                  <option value="">All Genders</option>
                  <option value="MALE">Male</option>
                  <option value="FEMALE">Female</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="input"
                >
                  <option value="createdAt">Registration Date</option>
                  <option value="user.firstName">First Name</option>
                  <option value="user.lastName">Last Name</option>
                  <option value="dateOfBirth">Date of Birth</option>
                  <option value="patientNumber">Patient Number</option>
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value as 'asc' | 'desc')}
                  className="input"
                >
                  <option value="desc">Newest First</option>
                  <option value="asc">Oldest First</option>
                </select>
              </div>

              <div className="flex items-end">
                <button
                  onClick={() => {
                    setSearchTerm('');
                    setGenderFilter('');
                    setSortBy('createdAt');
                    setSortOrder('desc');
                    setCurrentPage(1);
                  }}
                  className="btn-secondary w-full"
                >
                  Clear Filters
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Desktop Table View */}
      <div className="hidden lg:block bg-white rounded-lg shadow overflow-hidden">
        <div className="overflow-x-auto">
          {loading ? (
            <div className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="h-16 bg-gray-200 rounded animate-pulse"></div>
                ))}
              </div>
            </div>
          ) : (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('patientNumber')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Patient #</span>
                      {sortBy === 'patientNumber' && (
                        <span className="text-primary-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('user.firstName')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Patient</span>
                      {sortBy === 'user.firstName' && (
                        <span className="text-primary-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Contact
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Details
                  </th>
                  <th 
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                    onClick={() => handleSort('createdAt')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created</span>
                      {sortBy === 'createdAt' && (
                        <span className="text-primary-600">
                          {sortOrder === 'asc' ? '↑' : '↓'}
                        </span>
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {patients.map((patient) => (
                  <tr key={patient.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {patient.patientNumber}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="h-10 w-10 bg-primary-100 rounded-full flex items-center justify-center">
                          <span className="text-primary-600 font-medium text-sm">
                            {patient.user.firstName[0]}{patient.user.lastName[0]}
                          </span>
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900">
                            {patient.user.firstName} {patient.user.lastName}
                          </div>
                          <div className="text-sm text-gray-500">
                            {patient.user.email}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div>
                        <div>{patient.user.phone}</div>
                        <div className="text-gray-500 text-xs">
                          {formatGender(patient.gender)} • Age {calculateAge(patient.dateOfBirth)}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="text-xs">
                        <div className="text-gray-500">DOB: {formatDate(patient.dateOfBirth)}</div>
                        {patient.address?.city && patient.address?.state && (
                          <div className="text-gray-500 truncate max-w-32">
                            {patient.address.city}, {patient.address.state}
                          </div>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {formatDate(patient.createdAt)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <div className="flex items-center justify-end space-x-2">
                        <button
                          onClick={() => handleViewDetails(patient)}
                          className="text-primary-600 hover:text-primary-900 p-1 rounded-lg hover:bg-primary-50"
                          title="View Details"
                        >
                          <EyeIcon className="h-4 w-4" />
                        </button>
                        {canEdit && (
                          <button
                            onClick={() => handleEditPatient(patient)}
                            className="text-gray-600 hover:text-gray-900 p-1 rounded-lg hover:bg-gray-50"
                            title="Edit Patient"
                          >
                            <EditIcon className="h-4 w-4" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
            <div className="flex-1 flex justify-between sm:hidden">
              <button 
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <button 
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="btn-secondary disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
              <div>
                <p className="text-sm text-gray-700">
                  Showing <span className="font-medium">{((currentPage - 1) * itemsPerPage) + 1}</span> to{' '}
                  <span className="font-medium">{Math.min(currentPage * itemsPerPage, totalItems)}</span> of{' '}
                  <span className="font-medium">{totalItems}</span> results
                </p>
              </div>
              <div>
                <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px">
                  <button 
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="btn-secondary rounded-l-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Previous
                  </button>
                  <button className="btn-primary">{currentPage}</button>
                  <button 
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="btn-secondary rounded-r-md disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                </nav>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Mobile Card View */}
      <div className="lg:hidden space-y-4">
        {patients.map((patient) => (
          <div key={patient.id} className="bg-white rounded-lg shadow p-4 border border-gray-200">
            <div className="flex items-start justify-between">
              <div className="flex items-start space-x-3 flex-1 min-w-0">
                <div className="h-12 w-12 bg-primary-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-primary-600 font-medium text-sm">
                    {patient.user.firstName[0]}{patient.user.lastName[0]}
                  </span>
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm font-medium text-gray-900 truncate">
                      {patient.user.firstName} {patient.user.lastName}
                    </h3>
                    <span className="text-xs font-medium text-gray-500 ml-2 flex-shrink-0">
                      #{patient.patientNumber}
                    </span>
                  </div>
                  <p className="text-sm text-gray-500 truncate">{patient.user.email}</p>
                  <p className="text-sm text-gray-500">{patient.user.phone}</p>
                  <div className="mt-2 flex items-center justify-between text-xs text-gray-500">
                    <span>{formatGender(patient.gender)} • Age {calculateAge(patient.dateOfBirth)}</span>
                    <span>{formatDate(patient.createdAt)}</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Mobile Actions */}
            <div className="mt-4 flex items-center justify-end space-x-2 pt-3 border-t border-gray-100">
              <button
                onClick={() => handleViewDetails(patient)}
                className="flex items-center px-3 py-1.5 text-xs font-medium text-primary-600 bg-primary-50 rounded-lg hover:bg-primary-100 transition-colors"
              >
                <EyeIcon className="h-3 w-3 mr-1" />
                View
              </button>
              {canEdit && (
                <button
                  onClick={() => handleEditPatient(patient)}
                  className="flex items-center px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <EditIcon className="h-3 w-3 mr-1" />
                  Edit
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Add Patient Modal */}
      <AddPatientModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onPatientAdded={() => {
          fetchPatients();
          setShowAddModal(false);
        }}
      />

      {/* Patient Details Modal */}
      <PatientDetailsModal
        isOpen={showDetailsModal}
        onClose={() => {
          setShowDetailsModal(false);
          setSelectedPatientId(null);
        }}
        patientId={selectedPatientId}
      />

      {/* Edit Patient Modal */}
      <EditPatientModal
        isOpen={showEditModal}
        onClose={() => {
          setShowEditModal(false);
          setSelectedPatient(null);
        }}
        onPatientUpdated={() => {
          fetchPatients();
          setShowEditModal(false);
          setSelectedPatient(null);
        }}
        patient={selectedPatient}
      />
    </div>
  );
};

export default PatientsPage; 